import React, { useState, useMemo, useEffect } from 'react';
import Breadcrumb from '../../components/common/Breadcrumb';
import toast from 'react-hot-toast';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconDownload, IconSend, IconCheck, IconX, IconAlertCircle,
  IconGlobe, IconUsers, IconCurrencyNaira, IconChevronLeft, IconUserPlus,
  IconUserEdit, IconChevronDown, IconChevronUp,
} from '@tabler/icons-react';
import {
  usePayrollRun,
  usePayrollRunRecords,
  usePayrollRunSummary,
  useReviewPayrollRun,
  useApprovePayrollRun,
  useRejectPayrollRun,
  usePublishPayrollRun,
  useDownloadPayrollRun,
  useAddTemplateRecord,
  useUpdatePayrollRunAssignees,
} from '../../hooks/usePayroll';
import { useEmployees } from '../../hooks/useEmployees';
import { useAuth } from '../../contexts/AuthContext';
import ConfirmDeleteModal from '../../components/common/ConfirmDeleteModal';
import { PageLoader, TableLoader } from '../../components/common/Loader';
import AssigneeSearchField from './components/AssigneeSearchField';
import ReviewerChainField from './components/ReviewerChainField';
import ReviewStepsTimeline from './components/ReviewStepsTimeline';
import { getAssigneeFlags, isAnyReviewerOrApprover } from './utils';

const fmt = (v) =>
  Number(v || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_STYLES = {
  draft: 'bg-warning/10 text-warning border-warning/30',
  in_review: 'bg-sky/10 text-sky border-sky/30',
  submitted: 'bg-sky/10 text-sky border-sky/30',
  approved: 'bg-success/10 text-success border-success/30',
  published: 'bg-purple-500/10 text-purple-500 border-purple-500/30',
  rejected: 'bg-danger/10 text-danger border-danger/30',
};

const STATUS_LABELS = {
  draft: 'Draft',
  in_review: 'Under Review',
  submitted: 'Submitted for Approval',
  approved: 'Approved',
  published: 'Published',
  rejected: 'Rejected',
};

function ReviewModal({ isOpen, onClose, run, orgId }) {
  const [notes, setNotes] = useState('');
  const [showSignatories, setShowSignatories] = useState(false);
  const [signatory1, setSignatory1] = useState(null);
  const [signatory2, setSignatory2] = useState(null);
  const reviewRun = useReviewPayrollRun();

  if (!isOpen) return null;

  const steps = run.review_steps || [];
  const currentIndex = steps.findIndex((s) => s.status !== 'reviewed');
  const isLastReviewer = currentIndex === -1 || currentIndex === steps.length - 1;

  const handleSubmit = () => {
    if (!notes.trim()) {
      toast.error('Notes are required to submit your review.');
      return;
    }
    const data = { notes: notes.trim() };
    if (signatory1) data.signatory_1_employee_id = signatory1.id;
    if (signatory2) data.signatory_2_employee_id = signatory2.id;

    reviewRun.mutate({ id: run.id, data, orgId }, {
      onSuccess: () => {
        toast.success(isLastReviewer ? 'Run submitted for approval.' : 'Review recorded — moved to the next reviewer.');
        onClose();
      },
      onError: (err) => toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Review failed.'),
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-15">
        <div className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[500px]">
          <div className="flex items-center justify-between p-20 border-b border-border">
            <h3 className="text-[16px]/[20px] font-semibold">Submit Review</h3>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
              <IconX size={20} />
            </button>
          </div>
          <div className="p-20 flex flex-col gap-15">
            <p className="text-sm text-text-secondary">
              {isLastReviewer
                ? `This is the final review. Submitting will send this run to the approver${run.approve_assignee_name ? ` (${run.approve_assignee_name})` : ''}.`
                : 'Submitting will move this run to the next reviewer in the chain.'}
            </p>
            <div>
              <label className="block text-sm font-medium mb-5">
                Notes <span className="text-danger">*</span>
              </label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reviewed and confirmed."
                rows={4}
                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
              />
            </div>

            <button
              type="button"
              onClick={() => setShowSignatories(s => !s)}
              className="flex items-center gap-5 text-sm font-medium text-primary self-start"
            >
              {showSignatories ? <IconChevronUp size={14} /> : <IconChevronDown size={14} />}
              Set signatories (optional)
            </button>
            {showSignatories && (
              <div className="flex flex-col gap-10 -mt-5">
                <p className="text-xs text-text-secondary">
                  Only used on the final review. Left blank, the payslip signature block falls back to the last reviewer (signatory 1) and approver (signatory 2).
                </p>
                <div>
                  <label className="block text-sm font-medium mb-5">Signatory 1</label>
                  <EmployeeComboBox orgId={orgId} value={signatory1} onSelect={setSignatory1} onClear={() => setSignatory1(null)} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-5">Signatory 2</label>
                  <EmployeeComboBox orgId={orgId} value={signatory2} onSelect={setSignatory2} onClear={() => setSignatory2(null)} />
                </div>
              </div>
            )}
          </div>
          <div className="flex gap-10 p-20 border-t border-border">
            <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={reviewRun.isPending}>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={reviewRun.isPending}
              className="btn btn-primary flex-1 flex items-center justify-center gap-5"
            >
              <IconSend size={16} />
              {reviewRun.isPending ? 'Submitting...' : 'Submit Review'}
            </button>
          </div>
        </div>
      </div>
      <div onClick={onClose} className="fixed z-[1000] inset-0 bg-black/50 backdrop-blur-[3px]" />
    </>
  );
}

function RejectModal({ isOpen, onClose, run, orgId }) {
  const [reason, setReason] = useState('');
  const rejectRun = useRejectPayrollRun();

  if (!isOpen) return null;

  const handleReject = () => {
    if (!reason.trim()) { toast.error('A rejection reason is required.'); return; }
    rejectRun.mutate({ id: run.id, reason: reason.trim(), orgId }, {
      onSuccess: () => { toast.success('Run rejected.'); onClose(); },
      onError: (err) => toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Rejection failed.'),
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-15">
        <div className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[480px]">
          <div className="flex items-center justify-between p-20 border-b border-border">
            <h3 className="text-[16px]/[20px] font-semibold text-danger">Reject Run</h3>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
              <IconX size={20} />
            </button>
          </div>
          <div className="p-20 flex flex-col gap-15">
            <p className="text-sm text-text-secondary">
              Provide a reason for rejection. HR will be notified and can revise and resubmit.
            </p>
            <div>
              <label className="block text-sm font-medium mb-5">
                Rejection Reason <span className="text-danger">*</span>
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Explain what needs to be corrected..."
                rows={4}
                className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[8px] text-sm focus:outline-none resize-none"
              />
            </div>
          </div>
          <div className="flex gap-10 p-20 border-t border-border">
            <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={rejectRun.isPending}>
              Cancel
            </button>
            <button
              onClick={handleReject}
              disabled={rejectRun.isPending}
              className="btn btn-danger flex-1 flex items-center justify-center gap-5"
            >
              <IconX size={16} />
              {rejectRun.isPending ? 'Rejecting...' : 'Reject Run'}
            </button>
          </div>
        </div>
      </div>
      <div onClick={onClose} className="fixed z-[1000] inset-0 bg-black/50 backdrop-blur-[3px]" />
    </>
  );
}

// Same-org employee search combobox — used for adding run records and picking signatories.
function EmployeeComboBox({ orgId, value, onSelect, onClear, placeholder = 'Search by name or email...' }) {
  const { data: employees } = useEmployees(orgId);
  const [search, setSearch] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const filtered = useMemo(() => {
    const list = Array.isArray(employees) ? employees : (employees?.results || []);
    if (!search.trim()) return list.slice(0, 20);
    const q = search.toLowerCase();
    return list.filter(e =>
      `${e.first_name} ${e.last_name}`.toLowerCase().includes(q) ||
      e.email?.toLowerCase().includes(q)
    ).slice(0, 20);
  }, [employees, search]);

  return (
    <div className="relative">
      <input
        type="text"
        value={dropdownOpen ? search : (value ? `${value.first_name} ${value.last_name}` : '')}
        onFocus={() => { setDropdownOpen(true); setSearch(''); }}
        onBlur={() => setTimeout(() => setDropdownOpen(false), 150)}
        onChange={(e) => setSearch(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-bg-light border border-border rounded-[4px] px-10 pr-30 py-[8px] text-sm focus:outline-none"
      />
      {value && (
        <button
          type="button"
          onClick={onClear}
          className="absolute right-10 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-main"
        >
          <IconX size={14} />
        </button>
      )}
      {dropdownOpen && (
        <div className="absolute z-10 top-full left-0 right-0 bg-bg-dark border border-border rounded-[4px] shadow-light mt-2 max-h-[180px] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-12 py-10 text-sm text-text-secondary">No employees found</div>
          ) : (
            filtered.map(emp => (
              <div
                key={emp.id}
                onMouseDown={() => { onSelect(emp); setDropdownOpen(false); }}
                className={`px-12 py-10 cursor-pointer text-sm hover:bg-bg-light transition-colors ${value?.id === emp.id ? 'bg-primary/5 text-primary font-medium' : 'text-text-main'}`}
              >
                <div className="font-medium">{emp.first_name} {emp.last_name}</div>
                {emp.email && <div className="text-text-secondary text-xs">{emp.email}</div>}
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const SALARY_FIELDS = [
  { key: 'basic_salary',        label: 'Basic Salary',          group: 'earnings' },
  { key: 'housing',             label: 'Housing',               group: 'earnings' },
  { key: 'transport',           label: 'Transport',             group: 'earnings' },
  { key: 'meal',                label: 'Meal',                  group: 'earnings' },
  { key: 'utility',             label: 'Utility',               group: 'earnings' },
  { key: 'entertainment',       label: 'Entertainment',         group: 'earnings' },
  { key: 'pension_employee',    label: 'Pension (Employee)',    group: 'deductions' },
  { key: 'pension_employer',    label: 'Pension (Employer)',    group: 'deductions' },
  { key: 'paye_tax',            label: 'PAYE Tax',              group: 'deductions' },
  { key: 'nhf',                 label: 'NHF',                   group: 'deductions' },
  { key: 'nhis',                label: 'NHIS',                  group: 'deductions' },
  { key: 'life_assurance',      label: 'Life Assurance',        group: 'deductions' },
  { key: 'loan_deduction',      label: 'Loan Deduction',        group: 'deductions' },
  { key: 'other_deductions',    label: 'Other Deductions',      group: 'deductions' },
];

const EMPTY_FORM = Object.fromEntries(SALARY_FIELDS.map(f => [f.key, '']));

function AddEmployeeModal({ isOpen, onClose, orgId, onSuccess }) {
  const addRecord = useAddTemplateRecord();
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const handleClearEmployee = () => {
    setSelectedEmployee(null);
  };

  const handleSubmit = () => {
    if (!selectedEmployee) { toast.error('Please select an employee.'); return; }
    if (!form.basic_salary) { toast.error('Basic Salary is required.'); return; }

    const data = {
      employee: selectedEmployee.id,
      ...Object.fromEntries(
        SALARY_FIELDS.map(f => [f.key, parseFloat(form[f.key]) || 0])
      ),
    };

    addRecord.mutate({ data, orgId }, {
      onSuccess: () => {
        toast.success(`${selectedEmployee.first_name} ${selectedEmployee.last_name} added to payroll.`);
        handleClearEmployee();
        setForm(EMPTY_FORM);
        onSuccess?.();
        onClose();
      },
      onError: (err) => toast.error(err?.response?.data?.error || err?.response?.data?.detail || err?.response?.data?.employee?.[0] || 'Failed to add employee.'),
    });
  };

  if (!isOpen) return null;

  const earnings = SALARY_FIELDS.filter(f => f.group === 'earnings');
  const deductions = SALARY_FIELDS.filter(f => f.group === 'deductions');

  return (
    <>
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-15">
        <div className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[600px] max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-20 border-b border-border">
            <h3 className="text-[16px]/[20px] font-semibold">Add Employee to Run</h3>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary"><IconX size={20} /></button>
          </div>

          <div className="p-20 flex flex-col gap-15 overflow-y-auto">
            {/* Employee Search — combobox pattern */}
            <div>
              <label className="block text-sm font-medium mb-5">Employee <span className="text-danger">*</span></label>
              <EmployeeComboBox orgId={orgId} value={selectedEmployee} onSelect={setSelectedEmployee} onClear={handleClearEmployee} />
            </div>

            {/* Earnings */}
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-8">Earnings</p>
              <div className="grid grid-cols-2 gap-10">
                {earnings.map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium mb-3">
                      {f.label}{f.key === 'basic_salary' && <span className="text-danger ml-2">*</span>}
                    </label>
                    <input
                      type="number"
                      min="0"
                      value={form[f.key]}
                      onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder="0.00"
                      className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[6px] text-sm focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Deductions */}
            <div>
              <p className="text-xs font-semibold text-text-secondary uppercase tracking-wide mb-8">Deductions</p>
              <div className="grid grid-cols-2 gap-10">
                {deductions.map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-medium mb-3">{f.label}</label>
                    <input
                      type="number"
                      min="0"
                      value={form[f.key]}
                      onChange={e => setForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                      placeholder="0.00"
                      className="w-full bg-bg-light border border-border rounded-[4px] px-10 py-[6px] text-sm focus:outline-none"
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-10 p-20 border-t border-border">
            <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={addRecord.isPending}>Cancel</button>
            <button
              onClick={handleSubmit}
              disabled={addRecord.isPending}
              className="btn btn-primary flex-1 flex items-center justify-center gap-5"
            >
              <IconUserPlus size={16} />
              {addRecord.isPending ? 'Adding...' : 'Add Employee'}
            </button>
          </div>
        </div>
      </div>
      <div onClick={onClose} className="fixed z-[1000] inset-0 bg-black/50 backdrop-blur-[3px]" />
    </>
  );
}

function EditChainModal({ isOpen, onClose, run, orgId }) {
  const [reviewers, setReviewers] = useState([]);
  const [approveAssignee, setApproveAssignee] = useState(null);
  const updateAssignees = useUpdatePayrollRunAssignees();

  useEffect(() => {
    if (isOpen && run) {
      const chain = [...(run.review_steps || [])]
        .sort((a, b) => a.position - b.position)
        .map((s) => ({ id: s.reviewer, name: s.reviewer_name }));
      setReviewers(chain);
      setApproveAssignee(run.approve_assignee ? { id: run.approve_assignee, name: run.approve_assignee_name } : null);
    }
  }, [isOpen, run]);

  if (!isOpen) return null;

  const handleSubmit = () => {
    if (!reviewers.length) { toast.error('Please add at least one reviewer.'); return; }
    if (!approveAssignee) { toast.error('Please select an approver.'); return; }

    updateAssignees.mutate({
      id: run.id,
      data: { reviewer_ids: reviewers.map((r) => r.id), approve_assignee_id: approveAssignee.id },
      orgId,
    }, {
      onSuccess: () => { toast.success('Reviewer chain updated.'); onClose(); },
      onError: (err) => toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Failed to update the chain.'),
    });
  };

  return (
    <>
      <div className="fixed inset-0 z-[1001] flex items-center justify-center p-15">
        <div className="bg-bg-dark rounded-lg shadow-light border border-border w-full max-w-[480px]">
          <div className="flex items-center justify-between p-20 border-b border-border">
            <h3 className="text-[16px]/[20px] font-semibold">Edit Reviewer Chain</h3>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary">
              <IconX size={20} />
            </button>
          </div>
          <div className="p-20 flex flex-col gap-15">
            <p className="text-sm text-text-secondary">
              Sending this replaces the whole chain. Anyone may be from another organisation.
            </p>
            <ReviewerChainField
              required
              orgId={orgId}
              value={reviewers}
              onChange={setReviewers}
              excludeIds={approveAssignee ? [approveAssignee.id] : []}
            />
            <AssigneeSearchField
              label="Approver"
              required
              orgId={orgId}
              value={approveAssignee}
              onSelect={setApproveAssignee}
              onClear={() => setApproveAssignee(null)}
              excludeIds={reviewers.map((r) => r.id)}
            />
          </div>
          <div className="flex gap-10 p-20 border-t border-border">
            <button onClick={onClose} className="btn btn-outline-secondary flex-1" disabled={updateAssignees.isPending}>
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={updateAssignees.isPending}
              className="btn btn-primary flex-1 flex items-center justify-center gap-5"
            >
              <IconUserEdit size={16} />
              {updateAssignees.isPending ? 'Saving...' : 'Save Chain'}
            </button>
          </div>
        </div>
      </div>
      <div onClick={onClose} className="fixed z-[1000] inset-0 bg-black/50 backdrop-blur-[3px]" />
    </>
  );
}

export default function PayrollRunDetail() {
  const { id: runId } = useParams();
  const orgId = localStorage.getItem('orgId') || '';
  const { hasAnyPermission, user } = useAuth();
  const navigate = useNavigate();

  const [showReview, setShowReview] = useState(false);
  const [showReject, setShowReject] = useState(false);
  const [showAddEmployee, setShowAddEmployee] = useState(false);
  const [showEditChain, setShowEditChain] = useState(false);
  const [confirmApprove, setConfirmApprove] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);

  const { data: run, isLoading: loadingRun } = usePayrollRun(runId, orgId);

  const { canReview, canApprove, canReject } = getAssigneeFlags(run);
  const isInChain = isAnyReviewerOrApprover(run, user);
  const isHR = hasAnyPermission(['rbacapp.pay_review']);
  const canPublish = hasAnyPermission(['rbacapp.pay_publish']);

  const canViewRunData = isInChain || isHR;
  const canSeeActions = isInChain || isHR || canPublish;
  const isEditableStatus = run?.status === 'draft' || run?.status === 'rejected';

  const { data: records, isLoading: loadingRecords, refetch: refetchRecords } = usePayrollRunRecords(runId, orgId, { enabled: canViewRunData });
  const { data: summary } = usePayrollRunSummary(runId, orgId, { enabled: canViewRunData });
  const approveRun = useApprovePayrollRun();
  const publishRun = usePublishPayrollRun();
  const downloadRun = useDownloadPayrollRun();

  const handleApprove = () => {
    approveRun.mutate({ id: runId, orgId }, {
      onSuccess: () => { toast.success('Run approved.'); setConfirmApprove(false); },
      onError: (err) => { toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Approval failed.'); setConfirmApprove(false); },
    });
  };

  const handlePublish = () => {
    publishRun.mutate({ id: runId, orgId }, {
      onSuccess: (data) => {
        toast.success(data.message || `Published. ${data.payslips_generated} payslips generated.`);
        setConfirmPublish(false);
      },
      onError: (err) => { toast.error(err?.response?.data?.error || err?.response?.data?.detail || 'Publish failed.'); setConfirmPublish(false); },
    });
  };

  if (loadingRun) {
    return (
      <>
        <Breadcrumb title="Payroll Run" path="Payroll / Runs / Loading..." />
        <TableLoader text="Loading run details..." />
      </>
    );
  }

  if (!run) {
    return (
      <>
        <Breadcrumb title="Payroll Run" path="Payroll / Runs / Not Found" />
        <TableLoader text="Run not found." />
      </>
    );
  }

  return (
    <>
      <Breadcrumb title={run.label} path={`Payroll / Runs / ${run.label}`} />

      <button
        onClick={() => navigate(window.history.state?.idx > 0 ? -1 : '/payroll/runs')}
        className="btn btn-outline-secondary flex items-center gap-5 mb-15"
      >
        <IconChevronLeft size={16} />
        Back
      </button>

      <div>
        <div className="flex items-center gap-10 flex-wrap">
          <h1 className="text-[20px]/[24px] font-semibold">{run.label}</h1>
          <span className={`px-10 py-4 rounded-full text-xs capitalize border ${STATUS_STYLES[run.status] || ''}`}>
            {STATUS_LABELS[run.status] || run.status}
          </span>
        </div>
        <p className="text-text-secondary text-sm mt-5">
          Triggered by {run.triggered_by_name} &bull;{' '}
          {new Date(run.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
          {run.record_count > 0 && ` · ${run.record_count} employees`}
        </p>
        {(run.approved_by_name || run.published_by_name) && (
          <p className="text-text-secondary text-xs mt-5">
            {run.approved_by_name && <>Approved by {run.approved_by_name}{run.approved_at && ` on ${new Date(run.approved_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`}. </>}
            {run.published_by_name && <>Published by {run.published_by_name}{run.published_at && ` on ${new Date(run.published_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`}.</>}
          </p>
        )}
      </div>

      <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 my-20">
        <div className="flex items-center justify-between mb-15">
          <h2 className="text-[15px]/[20px] font-semibold">Review Chain</h2>
          {isHR && isEditableStatus && (
            <button
              onClick={() => setShowEditChain(true)}
              className="flex items-center gap-3 text-primary text-xs font-medium"
            >
              <IconUserEdit size={13} /> Edit
            </button>
          )}
        </div>
        <ReviewStepsTimeline
          reviewSteps={run.review_steps || []}
          approverName={run.approve_assignee_name}
          approverId={run.approve_assignee}
          runStatus={run.status}
          currentUserEmployeeId={user?.employeeId}
        />
      </div>

      {/* Rejection reason banner */}
      {run.status === 'rejected' && run.rejection_reason && (
        <div className="bg-danger/10 border border-danger/30 rounded-lg p-15 mb-20 flex gap-10">
          <IconAlertCircle size={20} className="text-danger shrink-0 mt-1" />
          <div>
            <p className="font-medium text-danger mb-3">Rejected</p>
            <p className="text-sm">{run.rejection_reason}</p>
          </div>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-15 mb-20">
          <div className="bg-bg-dark rounded-lg shadow-light p-15">
            <div className="flex items-center gap-10 mb-5">
              <IconUsers size={18} className="text-sky" />
              <span className="text-text-secondary text-sm">Employees</span>
            </div>
            <p className="text-[22px]/[28px] font-semibold">{summary.employee_count}</p>
          </div>
          <div className="bg-bg-dark rounded-lg shadow-light p-15">
            <div className="flex items-center gap-10 mb-5">
              <IconCurrencyNaira size={18} className="text-success" />
              <span className="text-text-secondary text-sm">Total Gross</span>
            </div>
            <p className="text-[18px]/[24px] font-semibold">₦{fmt(summary.total_gross)}</p>
          </div>
          <div className="bg-bg-dark rounded-lg shadow-light p-15">
            <div className="flex items-center gap-10 mb-5">
              <IconCurrencyNaira size={18} className="text-primary" />
              <span className="text-text-secondary text-sm">Total Net</span>
            </div>
            <p className="text-[18px]/[24px] font-semibold">₦{fmt(summary.total_net)}</p>
          </div>
          <div className="bg-bg-dark rounded-lg shadow-light p-15">
            <div className="flex items-center gap-10 mb-5">
              <IconCurrencyNaira size={18} className="text-danger" />
              <span className="text-text-secondary text-sm">Total PAYE</span>
            </div>
            <p className="text-[18px]/[24px] font-semibold">₦{fmt(summary.total_paye)}</p>
          </div>
        </div>
      )}

      {/* Workflow Actions */}
      {canSeeActions && (
        <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15 mb-20">
          <h2 className="text-[15px]/[20px] font-semibold mb-15">Workflow Actions</h2>
          <div className="flex gap-10 flex-wrap items-center">
            {/* Download — assigned reviewer/approver, or in-org HR */}
            {canViewRunData && (
              <button
                onClick={() => downloadRun.mutate({ id: runId, label: run.label, orgId })}
                disabled={downloadRun.isPending}
                className="btn btn-outline-secondary flex items-center gap-5"
              >
                <IconDownload size={16} />
                {downloadRun.isPending ? 'Downloading...' : 'Download Excel'}
              </button>
            )}

            {/* Review — current reviewer in the chain */}
            {canReview && (
              <button
                onClick={() => setShowReview(true)}
                className="btn btn-primary flex items-center gap-5"
              >
                <IconSend size={16} />
                {run.status === 'rejected' ? 'Re-submit Review' : 'Submit Review'}
              </button>
            )}

            {/* Approve — the approver, once all reviews are done */}
            {canApprove && (
              <button
                onClick={() => setConfirmApprove(true)}
                className="btn btn-primary flex items-center gap-5"
              >
                <IconCheck size={16} />
                Approve
              </button>
            )}

            {/* Reject — current reviewer or the approver */}
            {canReject && (
              <button
                onClick={() => setShowReject(true)}
                className="btn btn-danger flex items-center gap-5"
              >
                <IconX size={16} />
                Reject
              </button>
            )}

            {/* Publish — HR with pay_publish permission, approved */}
            {canPublish && run.status === 'approved' && (
              <button
                onClick={() => setConfirmPublish(true)}
                className="btn btn-primary flex items-center gap-5"
              >
                <IconGlobe size={16} />
                Publish Payslips
              </button>
            )}
          </div>
        </div>
      )}

      {/* Records Table */}
      <div className="bg-bg-dark rounded-lg shadow-light md:p-20 p-15">
        <div className="flex items-center justify-between mb-15 flex-wrap gap-10">
          <div>
            <h2 className="text-[15px]/[20px] font-semibold">
              Employee Snapshot ({records?.length ?? 0} records)
            </h2>
            <p className="text-xs text-text-secondary mt-3">
              These records reflect the payroll template at the time the run was triggered.
            </p>
          </div>
          {isHR && isEditableStatus && (
            <button
              onClick={() => setShowAddEmployee(true)}
              className="btn btn-outline-secondary flex items-center gap-5 text-sm"
            >
              <IconUserPlus size={15} />
              Add Employee
            </button>
          )}
        </div>
        {loadingRecords ? (
          <div className="text-center py-20 text-text-secondary">Loading records...</div>
        ) : !records?.length ? (
          <div className="text-center py-20 text-text-secondary">No records in this run.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[800px]">
              <thead>
                <tr className="bg-bg-light-100">
                  <th align="left" className="p-10 font-medium">Employee</th>
                  <th align="left" className="p-10 font-medium">Position</th>
                  <th align="right" className="p-10 font-medium">Basic Salary</th>
                  <th align="right" className="p-10 font-medium">Gross Pay</th>
                  <th align="right" className="p-10 font-medium">PAYE</th>
                  <th align="right" className="p-10 font-medium">Pension (Emp.)</th>
                  <th align="right" className="p-10 font-medium">Net Pay</th>
                </tr>
              </thead>
              <tbody>
                {records.map((row) => (
                  <tr key={row.id} className="border-t border-border hover:bg-bg-light">
                    <td className="p-10">
                      <p className="font-medium">{row.employee_name || row.raw_name}</p>
                      <p className="text-xs text-text-secondary">{row.raw_email}</p>
                    </td>
                    <td className="p-10 text-text-secondary">{row.raw_position || '—'}</td>
                    <td className="p-10 text-right">₦{fmt(row.basic_salary)}</td>
                    <td className="p-10 text-right">₦{fmt(row.gross_pay)}</td>
                    <td className="p-10 text-right">₦{fmt(row.paye_tax)}</td>
                    <td className="p-10 text-right">₦{fmt(row.pension_employee)}</td>
                    <td className="p-10 text-right font-semibold">₦{fmt(row.net_pay)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modals */}
      <AddEmployeeModal
        isOpen={showAddEmployee}
        onClose={() => setShowAddEmployee(false)}
        orgId={orgId}
        onSuccess={refetchRecords}
      />
      <ReviewModal
        isOpen={showReview}
        onClose={() => setShowReview(false)}
        run={run}
        orgId={orgId}
      />
      <RejectModal
        isOpen={showReject}
        onClose={() => setShowReject(false)}
        run={run}
        orgId={orgId}
      />
      <EditChainModal
        isOpen={showEditChain}
        onClose={() => setShowEditChain(false)}
        run={run}
        orgId={orgId}
      />
      <ConfirmDeleteModal
        isOpen={confirmApprove}
        onClose={() => setConfirmApprove(false)}
        onConfirm={handleApprove}
        isLoading={approveRun.isPending}
        title="Approve Payroll Run"
        message={`You are about to approve "${run.label}". Once approved, it can be published to release payslips to all employees.`}
        confirmLabel="Approve"
        loadingLabel="Approving..."
        confirmClassName="btn btn-primary flex-1 flex items-center justify-center gap-5"
        headerIcon={<IconCheck size={32} className="text-primary" />}
        headerIconBg="bg-primary/10"
      />
      <ConfirmDeleteModal
        isOpen={confirmPublish}
        onClose={() => setConfirmPublish(false)}
        onConfirm={handlePublish}
        isLoading={publishRun.isPending}
        title="Publish Payslips"
        message={`Publishing "${run.label}" will generate and release payslips for all ${run.record_count} employees. This cannot be undone.`}
        confirmLabel="Publish Now"
        loadingLabel="Publishing..."
        confirmClassName="btn btn-primary flex-1 flex items-center justify-center gap-5"
        headerIcon={<IconGlobe size={32} className="text-primary" />}
        headerIconBg="bg-primary/10"
      />
    </>
  );
}
